import React from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import type { StandardizedTestsMultiYearFramework } from '@backrs_ui/grow-ui/src/pages/StandardizedTestsGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type MultiYearFrameworkSectionProps = Readonly<{
  content: StandardizedTestsMultiYearFramework;
}>;

export const MultiYearFrameworkSection: React.FC<MultiYearFrameworkSectionProps> = ({
  content,
}) => (
  <Box sx={containerSx}>
    <Box sx={gridSx}>
      {content.phases.map((phase) => (
        <Box key={phase.id} sx={cardSx}>
          <Box sx={headerSx}>
            <Box sx={gradeChipSx}>
              <Box sx={gradeChipIconSx}>
                <AppIcon iconName="GraduationHat01" diameter="14px" color="currentColor" />
              </Box>
              <Typography variant="text-xs-medium" sx={gradeChipTextSx}>
                {phase.grade}
              </Typography>
            </Box>
            <Typography variant="text-sm-semibold" sx={titleSx}>
              {phase.title}
            </Typography>
          </Box>
          <Box sx={listSx}>
            {phase.points.map((point) => (
              <Typography key={point} variant="text-sm-regular" sx={bodyTextSx}>
                {point}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  pb: backrs.spacing['spacing-4xl'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  overflow: 'hidden',
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  paddingX: backrs.spacing['spacing-2xl'],
  paddingY: backrs.spacing['spacing-lg'],
});

const gradeChipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  borderRadius: backrs.radius['radius-sm'],
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
  width: 'fit-content',
});

const gradeChipIconSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  display: 'flex',
  alignItems: 'center',
});

const gradeChipTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  padding: backrs.spacing['spacing-2xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const bodyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
